import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  BookOpen,
  Search,
  Calendar,
  Clock,
  ChevronRight,
  Loader2,
  Filter,
  GraduationCap,
  Building2,
  AlertCircle,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useMyClasses } from '../hooks/useProfessor';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import type { ProfessorClassInfo, GetMyClassesQueryDto } from '@/types/professor.types';
import { getDayName } from '@/types/professor.types';

const MyClassesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { tokens } = useAuthStore();

  // Filters state
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string | undefined>(undefined);
  const [selectedSemester, setSelectedSemester] = useState<number | undefined>(undefined);
  const [selectedCampusId, setSelectedCampusId] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingYears, setIsLoadingYears] = useState(true);

  // Build query
  // The semester is filtered on the client so the page can tell which
  // semesters actually have classes and never offer an empty one.
  const query: GetMyClassesQueryDto = {
    academicYearId: selectedAcademicYearId,
  };

  // Fetch classes
  const { data: classes = [], isLoading, error, refetch } = useMyClasses(query);

  // Courses carry an absolute semester (S1…S6): S1/S3/S5 belong to the first
  // semester of the year, S2/S4/S6 to the second — matched by parity.
  const semesterParity = (absolute?: number | null): 1 | 2 | null => {
    if (!absolute || absolute < 1) return null;
    return ((((absolute - 1) % 2) + 1) as 1 | 2);
  };

  const availableSemesters = useMemo(() => {
    const slots = new Set<number>();
    classes.forEach((cls: ProfessorClassInfo) => {
      const parity = semesterParity(cls.semester);
      if (parity) slots.add(parity);
    });
    return Array.from(slots);
  }, [classes]);

  // Extract unique campuses from classes
  const campuses = useMemo(() => {
    const campusMap = new Map<string, { id: string; name: string }>();
    classes.forEach((cls: ProfessorClassInfo) => {
      if (cls.campusId && cls.campusName) {
        campusMap.set(cls.campusId, { id: cls.campusId, name: cls.campusName });
      }
    });
    return Array.from(campusMap.values());
  }, [classes]);

  // Show campus filter only if multiple campuses
  const showCampusFilter = campuses.length > 1;

  // Helper function to determine current semester based on today's date
  const getCurrentSemester = (year: AcademicYear): number | undefined => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if we're in semester 1 using explicit dates
    if (year.semester1StartDate && year.semester1EndDate) {
      const s1Start = new Date(year.semester1StartDate);
      const s1End = new Date(year.semester1EndDate);
      s1Start.setHours(0, 0, 0, 0);
      s1End.setHours(23, 59, 59, 999);

      if (today >= s1Start && today <= s1End) {
        return 1;
      }
    }

    // Check if we're in semester 2 using explicit dates
    if (year.semester2StartDate && year.semester2EndDate) {
      const s2Start = new Date(year.semester2StartDate);
      const s2End = new Date(year.semester2EndDate);
      s2Start.setHours(0, 0, 0, 0);
      s2End.setHours(23, 59, 59, 999);

      if (today >= s2Start && today <= s2End) {
        return 2;
      }
    }

    // Fallback: If semester dates are not configured, infer from academic year dates
    // Assume S1 = first half of academic year, S2 = second half
    if (year.startDate && year.endDate) {
      const yearStart = new Date(year.startDate);
      const yearEnd = new Date(year.endDate);
      yearStart.setHours(0, 0, 0, 0);
      yearEnd.setHours(23, 59, 59, 999);

      // Only proceed if today is within the academic year
      if (today >= yearStart && today <= yearEnd) {
        // Calculate midpoint of the academic year
        const midpoint = new Date(yearStart.getTime() + (yearEnd.getTime() - yearStart.getTime()) / 2);

        if (today < midpoint) {
          return 1;
        } else {
          return 2;
        }
      }
    }

    // If not within any semester dates, return undefined (show all)
    return undefined;
  };

  // Load academic years on mount
  useEffect(() => {
    const loadAcademicYears = async () => {
      if (!tokens?.accessToken) return;

      setIsLoadingYears(true);
      try {
        const years = await fetchAcademicYears(tokens.accessToken);
        setAcademicYears(years);

        // A year carried in the URL wins: arriving from a dashboard card that
        // counted a closed year must land on that same year, not silently
        // switch the subject back to the current one.
        const requestedId = searchParams.get('academicYearId');
        const requested = requestedId ? years.find((y: AcademicYear) => y.id === requestedId) : null;
        const currentYear = years.find((y: AcademicYear) => y.isCurrent);
        const initial = requested ?? currentYear;

        if (initial) {
          setSelectedAcademicYearId(initial.id);

          // The semester only auto-selects for the live year; a closed one has
          // no "current" semester, so everything is shown.
          if (initial.isCurrent) {
            const currentSemester = getCurrentSemester(initial);
            if (currentSemester) {
              setSelectedSemester(currentSemester);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load academic years:', err);
      } finally {
        setIsLoadingYears(false);
      }
    };

    loadAcademicYears();
  }, [tokens?.accessToken]);

  // Filter classes by search query and campus
  const filteredClasses = useMemo(() => {
    return classes.filter((cls: ProfessorClassInfo) => {
      // Classes with no semester attached are never hidden: missing metadata
      // must not make a course disappear.
      if (selectedSemester) {
        const parity = semesterParity(cls.semester);
        if (parity !== null && parity !== selectedSemester) return false;
      }
      // Campus filter
      if (selectedCampusId && cls.campusId !== selectedCampusId) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        return (
          cls.moduleElementName.toLowerCase().includes(search) ||
          cls.moduleName.toLowerCase().includes(search) ||
          cls.groupeName.toLowerCase().includes(search) ||
          cls.filiereName.toLowerCase().includes(search) ||
          cls.filiereCode.toLowerCase().includes(search)
        );
      }

      return true;
    });
  }, [classes, selectedCampusId, searchQuery, selectedSemester]);

  // Group courses by filière + niveau so long lists stay readable
  const groupedClasses = useMemo(() => {
    const groups = new Map<string, ProfessorClassInfo[]>();
    for (const cls of filteredClasses) {
      const key = `${cls.filiereName} — ${cls.niveau === 1 ? '1ère' : `${cls.niveau}ème`} Année`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(cls);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b, 'fr'));
  }, [filteredClasses]);

  // Navigate to class detail — keep the current role segment (this page is
  // shared by teachers AND staff who also teach: /teacher, /filiere, /dp, …)
  const handleClassClick = (cls: ProfessorClassInfo) => {
    const basePath = location.pathname.split('/mes-cours')[0];
    const url = `${basePath}/mes-cours/${cls.groupeId}/module/${cls.moduleElementId}`;
    const params = selectedAcademicYearId ? `?academicYearId=${selectedAcademicYearId}` : '';
    navigate(`${url}${params}`);
  };

  // Render loading state
  if (isLoadingYears) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mes Cours</h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez vos cours, présences et notes
              </p>
            </div>

            {/* Stats badge */}
            <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg">
              <BookOpen className="h-5 w-5" />
              <span className="font-medium">{filteredClasses.length} Cours</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un cours, groupe ou filière..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Campus Filter - Only show if multiple campuses */}
            {showCampusFilter && (
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  value={selectedCampusId || ''}
                  onChange={(e) => setSelectedCampusId(e.target.value || undefined)}
                  className="appearance-none pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">Tous les campus</option>
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Academic Year Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={selectedAcademicYearId || ''}
                onChange={(e) => setSelectedAcademicYearId(e.target.value || undefined)}
                className="appearance-none pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">Toutes les années</option>
                {academicYears.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name} {year.isCurrent && '(En cours)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Semester Filter */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={selectedSemester ?? ''}
                onChange={(e) => setSelectedSemester(e.target.value ? Number(e.target.value) : undefined)}
                className="appearance-none pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">Tous les semestres</option>
                {/* Only semesters the professor actually has classes in: a year
                    whose S2 has not started must not offer an empty tab. */}
                {availableSemesters.includes(1) && <option value="1">Semestre 1</option>}
                {availableSemesters.includes(2) && <option value="2">Semestre 2</option>}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement des cours. {(error as Error).message}</span>
            <button
              onClick={() => refetch()}
              className="ml-auto text-red-700 underline hover:no-underline"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredClasses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun cours trouvé</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery
                ? 'Essayez de modifier votre recherche'
                : "Vous n'avez aucun cours assigné pour cette période"}
            </p>
          </div>
        )}

        {/* Classes grouped by filière + niveau */}
        {!isLoading && filteredClasses.length > 0 && (
          <div className="space-y-8">
            {groupedClasses.map(([groupLabel, groupClasses]) => (
              <section key={groupLabel}>
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="h-5 w-5 text-purple-600" />
                  <h2 className="text-base font-semibold text-gray-900">{groupLabel}</h2>
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {groupClasses.length} cours
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {groupClasses.map((cls: ProfessorClassInfo) => (
                    <div
                      key={`${cls.groupeId}-${cls.moduleElementId}`}
                      onClick={() => handleClassClick(cls)}
                      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
                    >
                      {/* Title and Arrow */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                            {cls.moduleElementName}
                          </h3>
                          <p className="text-sm text-gray-500 truncate">
                            {cls.moduleCode} - {cls.moduleName}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5 group-hover:text-blue-500 transition-colors" />
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                          <GraduationCap className="h-3.5 w-3.5" />
                          {cls.groupeName}
                          {cls.sharedWithGroups && cls.sharedWithGroups.length > 0 && (
                            <span title="Séance partagée : les groupes suivent le cours ensemble">
                              {' '}+ {cls.sharedWithGroups.join(' + ')}
                            </span>
                          )}
                        </span>
                        {cls.campusName && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            <Building2 className="h-3 w-3" />
                            {cls.campusName}
                          </span>
                        )}
                      </div>

                      {/* Schedule */}
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="truncate">
                          {cls.scheduleInfo.length > 0 ? (
                            <>
                              {cls.scheduleInfo.slice(0, 2).map((schedule, index) => (
                                <span key={index}>
                                  {index > 0 && ' • '}
                                  {getDayName(schedule.dayOfWeek)} {schedule.startTime}-{schedule.endTime}
                                </span>
                              ))}
                              {cls.scheduleInfo.length > 2 && (
                                <span className="text-gray-400"> +{cls.scheduleInfo.length - 2}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 italic">Pas d'horaire défini</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyClassesPage;
